import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building, Search, Edit, Trash2, Eye, Loader2, Mail, Phone,
  MapPin, Briefcase, RefreshCw, Globe, Users, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEmployersPage = () => {
  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employersRes, jobsRes] = await Promise.all([
        axios.get(`${API}/admin/users?role=employer`),
        axios.get(`${API}/jobs`)
      ]);
      setEmployers(employersRes.data || []);
      setJobs(jobsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const getEmployerJobCount = (employerId) => {
    return jobs.filter(j => j.employer_id === employerId).length;
  };

  const filteredEmployers = employers.filter(emp => {
    return emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           emp.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-employers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employers Management</h1>
          <p className="text-slate-500 mt-1">{employers.length} employers, {jobs.length} jobs posted</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Employers</p>
              <p className="text-xl font-bold text-slate-900">{employers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Jobs</p>
              <p className="text-xl font-bold text-slate-900">{jobs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Jobs</p>
              <p className="text-xl font-bold text-slate-900">{jobs.filter(j => j.is_active).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">This Month</p>
              <p className="text-xl font-bold text-slate-900">
                {employers.filter(e => {
                  if (!e.created_at) return false;
                  const date = new Date(e.created_at);
                  const now = new Date();
                  return date.getMonth() === now.getMonth();
                }).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search employers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Employer</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Contact</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Jobs Posted</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Joined</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredEmployers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No employers found</td>
              </tr>
            ) : (
              filteredEmployers.map((employer) => (
                <tr key={employer.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Building className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{employer.company_name || employer.name}</p>
                        <p className="text-sm text-slate-500">{employer.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {employer.phone && (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Phone className="w-3 h-3" />
                        {employer.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      {getEmployerJobCount(employer.id)} jobs
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {employer.created_at ? new Date(employer.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setSelectedEmployer(employer); setViewDialogOpen(true); }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Employer Details</DialogTitle>
          </DialogHeader>
          {selectedEmployer && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <Building className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedEmployer.company_name || selectedEmployer.name}</h3>
                  <p className="text-slate-500">{selectedEmployer.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-slate-500">Phone</p>
                  <p className="font-medium">{selectedEmployer.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Jobs Posted</p>
                  <p className="font-medium">{getEmployerJobCount(selectedEmployer.id)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Industry</p>
                  <p className="font-medium">{selectedEmployer.industry || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Website</p>
                  <p className="font-medium">{selectedEmployer.website || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEmployersPage;
